/* eslint-disable no-console */
import {
  parseFolders,
  resultsLimit,
  virtualPeriodValues,
} from "../../../main/src/utils/AdvSearchTypes";
import {getBookIdWithPrefix, getBookIdNumber} from "../../utils/content/content";
import {
  fixSearchPreferExact
} from "../../utils/search/search";
import {parseResponseDefault} from "../not-sorted.utils";
import {makeRequest} from "../baseApi";
import {logSearchEvent, SearchLogEvents} from "../analitics";
import {normalizeSearchBasicResponse} from "./utils";
import {advSearch} from "./advanced/requests";
import {relevantSearchRequest} from "./related/requests";
import {
  advSearchTypes,
  FOLDERS_IDS_APL, Period,
  PeriodWithCustom, QUERY_TYPE, SEARCH_TOTAL_MAX,
  SearchType
} from "../../utils/search/search.constants";
import {DEFAULT_LANGUAGE} from "../../utils/multilang/constants";
import { makeParamsString } from "../utils";
import { getBiblePubnr } from "@/shared/utils/search/advanced/advanced";

export function basicSearchRequest({
  query,
  offset,
  folders,
  books,
  chapters,
  extras,
  limit,
  langs,
}) {
  let postData = {
    query,
    limit: limit ?? resultsLimit,
    offset: offset,
    facets: true,
    include_language_facets: true,
  };

  const apiExtras = extras ? { ...extras } : {};

  if (apiExtras.queryType) {
    apiExtras.query_type = apiExtras.queryType;
    delete apiExtras.queryType;
  }

  if (extras?.period) {
    apiExtras.period = virtualPeriodValues.includes(extras.period)
      ? PeriodWithCustom.all : extras.period;
  }

  delete apiExtras["type"];
  delete apiExtras["treePeriod"];

  if (extras?.period === PeriodWithCustom.myBible && !chapters?.length) {
    postData.pubnr = [getBiblePubnr(apiExtras["pubnr"] || books?.[0])];
  } else if (extras?.period === PeriodWithCustom.apl) {
    postData.folder = FOLDERS_IDS_APL;
  } else {
    if (folders && folders.length === 1 && folders[0] === "all") {
      const defLang = extras?.defLang || DEFAULT_LANGUAGE;
      delete apiExtras["defLang"];
      postData.lang = [defLang, "all"];
    } else if (folders && folders.length) {
      const { lang, folder, pubnr } = parseFolders(folders);
      postData.folder = folder;
      postData.pubnr = pubnr;
      postData.lang = lang;
      delete apiExtras["lang"];
    }

    if (langs) {
      postData.lang = langs;
    }

    if (books && books.length) {
      const { pubnr } = parseFolders(books.map(getBookIdWithPrefix));
      postData.pubnr = pubnr;
      postData.period = PeriodWithCustom.all;
    }

    if (chapters?.length) {
      postData.chapter = chapters;
    }
  }

  if (apiExtras) {
    postData = {
      ...apiExtras,
      ...postData,
    };
  }

  if (postData.lang) {
    if (typeof postData.lang === "string") {
      postData.lang = [postData.lang];
    }
  } else {
    delete postData.lang;
  }

  if (!postData.snippet) {
    postData.snippet = "short";
  }

  logSearchEvent(SearchLogEvents.SEARCH, postData);
  // console.groupCollapsed(
  //   `🔎 SH ${query} (${postData.lang?.length ? postData.lang : "no lang"})`
  //   + ` [${postData.limit}]`
  //   + ` ${postData.query_type ? (postData.query_type + " ") : ""}`
  // );
  // console.table(Object.keys(postData).reduce((acc, key) => {
  //   if (postData[key]) {
  //     acc[key] = Array.isArray(postData[key]) ? postData[key].toString() : postData[key];
  //   }
  //   return acc;
  // }, {}));
  // console.groupEnd();

  const url = "search";
  const cacheName = url + JSON.stringify(postData);

  return makeRequest({
    url,
    type: "post",
    memoryCache: true,
    memoryCacheName: cacheName,
    data: postData,
    parseResponse: (response) => normalizeSearchBasicResponse(query, response),
  });
}

async function splitSearchMatchRequest(params) {
  return fixSearchPreferExact((queryType, offset, limit) => {
    return basicSearchRequest({
      ...params,
      extras: { ...params.extras, queryType },
      offset,
      limit,
    });
  }, params.offset, params.limit);
}

const splitSearchRequest = async ({ query, offset, limit, folders, books, extras, langs }) => {
  const nIdBookFirst = getBookIdNumber(extras?.bookfirst);

  const fetchFn = extras.queryType === QUERY_TYPE.PREFER_EXACT
    ? splitSearchMatchRequest
    : basicSearchRequest;

  const check1 = await fetchFn({
    query,
    langs,
    folders,
    books: [nIdBookFirst],
    extras: { ...extras, period: Period.all, },

    offset: 0,
    limit: 1,
  });

  let total = check1.total;

  const results = [];
  if (total > offset) {
    const response1 = await fetchFn({
      query,
      langs,
      folders,
      books: [nIdBookFirst],
      extras: { ...extras, period: Period.all, },

      offset,
      limit,
    });
    results.push(...response1.results);
  }

  const req2Offset = offset + results.length - total;
  const req2Limit = limit - results.length;
  const needMoreResults = req2Limit > 0;
  const hasMoreResultsFromFirstPart =  req2Offset < SEARCH_TOTAL_MAX && req2Offset > -1
  if (needMoreResults && hasMoreResultsFromFirstPart) {
    const response2 = await fetchFn({
      query,
      folders,
      books,
      langs,
      extras: {
        ...extras,
        excluded_pubnr: [nIdBookFirst],
      },
      offset: req2Offset,
      limit: req2Limit,
    });

    const response2ResultsIndexUpdated = response2.results.map((item) => {
      return {
        ...item,
        index: item.index + total,
      };
    });

    total += response2.total;
    results.push(...response2ResultsIndexUpdated);
  } else {
    const check2 = await fetchFn({
      query,
      folders,
      books,
      langs,
      extras: {
        ...extras,
        excluded_pubnr: [nIdBookFirst],
      },
      offset: 0,
      limit: 1,
    });
    total += check2.total;
  }

  return { total, results, };
};

export const searchRequest = ({
  query,
  start,
  folders,
  books,
  context,
  extras,
  limit = resultsLimit,
  chapters,
  langs,
}) => {
  const offset = parseInt(start, 10);
  if (advSearchTypes.includes(extras?.type)) {
    return advSearch(query, extras, limit, offset, folders);
  }

  if (extras?.type === SearchType.related && extras?.lang?.length > 0) {
    return relevantSearchRequest(query, context, extras, limit, offset);
  } else if (extras?.type === SearchType.related) {
    return false;
  }

  if (extras?.bookfirst) {
    const extrasFinal = { ...extras };
    delete extras.bookfirst;

    return splitSearchRequest({
      query, offset, folders, books, extras: extrasFinal, limit
    });
  }

  if (extras?.queryType === QUERY_TYPE.PREFER_EXACT) {
    return splitSearchMatchRequest({
      query,
      offset,
      folders,
      books,
      context,
      extras,
      limit: limit || resultsLimit,
      chapters,
      langs,
    });
  }

  return basicSearchRequest({
    query,
    offset,
    folders,
    context,
    books,
    chapters,
    extras,
    langs,
    limit,
  });
};

export const searchBookByNameRequest = (query, langs, limit = 100) => {
  const params = [`query=${encodeURIComponent(query)}`];
  if (langs) {
    params.push(makeParamsString({ lang: langs }, true));
  }
  if (limit) {
    params.push(`limit=${limit}`);
  }

  return makeRequest({
    url: `search/book/name?${params.join("&")}`,
    memoryCache: true,
    parseResponse: (response) => {
      const { total, results } = response.data;
      return {
        total,
        results: results.map((item) => ({
          ...item,
          id: getBookIdWithPrefix(item.book_id),
          coverSmall: item.cover,
          folder_color_group: item.folder_type,
        })),
      };
    },
  });
};

export const getPosibleTranslateLangRequest = (lang) => {
  return makeRequest({
    url: "/translations/languages/" + lang,
    memoryCache: true,
    parseResponse: parseResponseDefault,
  });
};

export const getTranslateRequest = (langs, query) => {
  const { fromLang, toLang } = langs;
  return makeRequest({
    url: `/translations/translate?from=${fromLang}&to=${toLang}&text=${query}`,
    memoryCache: true,
    parseResponse: parseResponseDefault,
  });
};

